import { useCallback, useEffect, useState } from "react";
import {
  exportCampaign,
  getCampaign,
  getCampaignStatus,
  pauseCampaign,
  resumeCampaign,
  skipCampaignCondition,
  stopCampaign,
} from "services/experimentApi";
import { useStableState } from "utils/stableState";

export const CAMPAIGN_POLL_MS = 10000;
const DISMISSED_KEY = "padihat_experiment_campaign_dismissed";
const readDismissed = () => {
  try {
    return Number(localStorage.getItem(DISMISSED_KEY)) || null;
  } catch {
    return null;
  }
};
const ACTIVE = ["running", "paused", "needs_attention", "stopping"];

/**
 * The calibration campaign the Experiment tab shows: the chamber's active campaign, or `viewedCampaignId` (a past
 * one picked from the run history). Polled every CAMPAIGN_POLL_MS while it is active; once otherwise. All polled
 * state is stable (an identical answer changes nothing on screen) and a failed poll keeps what is shown.
 */
export function useCalibrationCampaign(controllerMac, viewedCampaignId = null) {
  const [activeId, setActiveId] = useStableState(null);
  // The newest campaign that has ended: shown (how it ended) until this browser closes it.
  const [lastId, setLastId] = useStableState(null);
  const [dismissedId, setDismissedId] = useState(readDismissed);
  const [campaign, setCampaign] = useStableState(null);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const refreshActive = useCallback(async () => {
    if (!controllerMac) return;
    try {
      const status = await getCampaignStatus(controllerMac);
      setActiveId(status.active_campaign ? status.active_campaign.id : null);
      setLastId(status.last_campaign ? status.last_campaign.id : null);
    } catch {
      // Keep what is shown; the next poll tries again.
    }
  }, [controllerMac, setActiveId, setLastId]);

  const showingLast = !viewedCampaignId && !activeId && Boolean(lastId) && lastId !== dismissedId;
  const shownId = viewedCampaignId || activeId || (showingLast ? lastId : null);

  const refreshCampaign = useCallback(async () => {
    if (!shownId) {
      setCampaign(null);
      return;
    }
    try {
      const result = await getCampaign(shownId);
      setCampaign(result.campaign);
      setError("");
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [shownId, setCampaign]);

  useEffect(() => {
    refreshActive();
    const timer = window.setInterval(refreshActive, CAMPAIGN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshActive]);

  const isActive = Boolean(campaign && ACTIVE.includes(campaign.state));
  useEffect(() => {
    refreshCampaign();
    if (!isActive) return undefined;
    const timer = window.setInterval(refreshCampaign, CAMPAIGN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshCampaign, isActive]);

  const act = useCallback(
    (call) => async () => {
      if (!campaign) return;
      setPending(true);
      setError("");
      try {
        const result = await call(campaign.id);
        if (result?.campaign) setCampaign(result.campaign);
        await refreshActive();
      } catch (requestError) {
        setError(requestError.data?.problems?.join("; ") || requestError.message);
      } finally {
        setPending(false);
      }
    },
    [campaign, refreshActive, setCampaign]
  );

  return {
    campaign,
    activeCampaignId: activeId,
    showingLast,
    dismissLast: () => {
      setDismissedId(lastId);
      try {
        localStorage.setItem(DISMISSED_KEY, String(lastId));
      } catch {
        // A per-browser convenience only.
      }
    },
    isActive,
    error,
    pending,
    pause: act(pauseCampaign),
    resume: act(resumeCampaign),
    stop: act(stopCampaign),
    skip: act(skipCampaignCondition),
    exportZip: act(exportCampaign),
    refresh: () => {
      refreshActive();
      refreshCampaign();
    },
    adopt: (started) => {
      setActiveId(started.id);
      setCampaign(started);
    },
  };
}
